import { isDashmoteStaffEmail } from "../slices/authSlice";
import { getRoleDisplayName, getRoleStyle } from "@dashone/user-scopes";

/**
 * Role utilities for User Management. The role display vocabulary (names + pill
 * styles) is the shared canonical source in `@dashone/user-scopes`; the helpers
 * below layer web-specific access affordances on top of it.
 */

// Re-export the shared display helpers so importers of `../roleUtils` resolve them.
export { getRoleDisplayName, getRoleStyle };

// Canonical (backend snake_case) form for every role representation seen in
// the app. Display names are NOT a mechanical transform of the backend value
// ("Super User" is super_admin), so normalization must go through this map.
const CANONICAL_ROLES = {
  super_admin: 'super_admin',
  'super admin': 'super_admin',
  'super user': 'super_admin',
  admin: 'admin',
  normal_user: 'normal_user',
  'normal user': 'normal_user',
};

/**
 * Normalize any role representation (backend snake_case or table display
 * string) to the backend snake_case form.
 * @param {string} roleValue - The role value (can be in any format)
 * @returns {string|null} - Canonical snake_case role, or null when absent
 */
export const normalizeRole = (roleValue) => {
  if (!roleValue) return null;
  const key = String(roleValue).trim().toLowerCase();
  return CANONICAL_ROLES[key] || key.replace(/\s+/g, '_');
};

/**
 * Cosmetic gate for the per-row "Reset" action in User Management. Mirrors
 * the backend hierarchy (which is the real authorization boundary): a
 * super_admin may reset anyone; a plain admin may reset only normal users;
 * everyone else gets no button.
 * @param {string} callerRole - Current user's role in the selected workspace
 * @param {string} targetRoleName - Target row's role (display string or raw)
 * @returns {boolean} - Whether the Reset action should be offered
 */
export const canResetPasswordRow = (callerRole, targetRoleName) => {
  const caller = normalizeRole(callerRole);
  if (caller === 'super_admin') return true;
  if (caller === 'admin') {
    return normalizeRole(targetRoleName) === 'normal_user';
  }
  return false;
};

// Roles that may reach the User Management / Settings pages within a workspace.
// Plain `admin` is included (unlike ADMIN_ROLES in moduleScopes) — the pages
// serve workspace admins, not just super admins.
export const USER_MANAGEMENT_ACCESS_ROLES = ['super_admin', 'admin'];

/**
 * Whether a user may access the User Management / Settings pages. This is the
 * single source of truth for both the route guard (UserManagementProtectedRoute)
 * and the profile-dropdown nav items, so the menu can never show a link the
 * route would redirect away from.
 *
 * Access is granted to any dashmote.com account, or to a user whose role in the
 * currently-selected workspace is super_admin / admin.
 *
 * @param {string|undefined} email - The signed-in user's email (state.auth.user.email)
 * @param {object|null} userData - state.auth.userData (carries the id_client role map)
 * @param {object|string|null} selectedWorkspace - state.auth.selectedWorkspace ({ key } or a raw id)
 * @returns {boolean}
 */
export const canAccessUserManagement = (email, userData, selectedWorkspace) => {
  if (isDashmoteStaffEmail(email)) return true;
  if (!userData || !selectedWorkspace) return false;
  const workspaceKey = selectedWorkspace?.key || selectedWorkspace;
  const role = userData?.id_client?.[workspaceKey]?.role;
  return USER_MANAGEMENT_ACCESS_ROLES.includes(normalizeRole(role));
};

// Copy shown in place of a role's description when the caller may not grant it.
// Shared by the invite RolePicker and the User Management Role column so both
// surfaces explain the same refusal in the same words.
//
// `assignableRoles` is the server-computed ladder from the roster response.
// `null` means NOT YET KNOWN (unfetched, or the fetch failed), which is a
// different sentence from a known-empty ladder: one is our problem, the other
// is the caller's permissions. Conflating them tells someone they lack
// permissions when the request simply did not land.
export const roleUnavailableReason = (role, assignableRoles) => {
  if (!Array.isArray(assignableRoles)) {
    return "Couldn't load your permissions — reload the page";
  }
  if (assignableRoles.length === 0) {
    return "You cannot grant roles in this workspace";
  }
  return role === 'super_admin'
    ? "Only a Super Admin can grant this role"
    : "You cannot grant this role";
};

/**
 * Whether `role` may be granted, per the server-computed ladder.
 *
 * The ladder comes from `assignable_roles` on the roster response, which the
 * API derives from the same function its write path enforces. The client no
 * longer mirrors that rule: a mirror was provably wrong for Dashmote staff,
 * because the client synthesizes a super_admin `id_client` entry for staff
 * (`ensureImplicitWorkspaceEntry`) that the server never grants.
 *
 * An unknown ladder (`null`) grants nothing. Failing open would offer a role
 * the API refuses, which is the whole defect this contract removes.
 *
 * @param {string} role - backend role value
 * @param {string[]|null|undefined} assignableRoles - the server's ladder
 * @returns {boolean}
 */
export const canAssignRole = (role, assignableRoles) =>
  Array.isArray(assignableRoles) && assignableRoles.includes(role);
